import React, { useEffect, useRef } from "react";
import RateDisclaimer from "./RateDisclaimer";

/**
 * Mortgage-quote.me pre-quote CTA form.
 *
 * The vendor script picks up the form ONLY if it's in the DOM at the moment
 * the script first runs. To guarantee timing independent of React lifecycle,
 * we inject the exact HTML snippet from the vendor's docs into a host div
 * via innerHTML — this includes the inline config <script> AND the loader
 * <script>, which the browser parses synchronously in DOM order.
 */
const COMPANY_ID = "69ea34dcffff3f4c2c9b5d36";

const VENDOR_SNIPPET = `
<form class="mqcta-pre-quote-form" data-question-set="[1,2,3]"></form>
<script>
  window.mqctaConfig = {
    modalSettings: {
      openOnLoad: false,
      closeButton: true,
      sidebar: { showLogo: false }
    }
  };
</script>
<script id="mortgage-cta" src="https://frontend.mortgage-quote.me/calculators/mortgage-cta/v1/mortgage-cta.min.js" data-company-id="${COMPANY_ID}" defer></script>
`;

export default function MortgageCTAForm() {
    const hostRef = useRef(null);

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return;

        // Always (re-)inject on mount so SPA navigations don't leave us with
        // a stale, empty host. Cache-bust the loader URL so the vendor IIFE
        // runs fresh.
        const container = document.createElement("div");
        container.innerHTML = VENDOR_SNIPPET.trim();

        const appended = [];
        Array.from(container.childNodes).forEach((node) => {
            if (node.tagName === "SCRIPT") {
                const s = document.createElement("script");
                if (node.src) {
                    s.src = node.src;
                }
                // Preserve ALL attributes (including `id`). The vendor
                // mortgage-cta.min.js looks itself up via
                // document.getElementById("mortgage-cta") to read its
                // data-company-id attribute — stripping the id makes the
                // vendor throw "Cannot read properties of null".
                for (const attr of node.attributes) {
                    if (attr.name === "src") continue;
                    s.setAttribute(attr.name, attr.value);
                }
                if (!node.src && node.textContent) {
                    s.textContent = node.textContent;
                }
                host.appendChild(s);
                appended.push(s);
            } else {
                host.appendChild(node);
                appended.push(node);
            }
        });

        return () => {
            appended.forEach((n) => {
                try {
                    n.remove();
                } catch (_) {
                    /* noop */
                }
            });
            if (host) host.innerHTML = "";
        };
    }, []);

    return (
        <section
            id="mortgage-cta-section" data-testid="mortgage-cta-section"
            className="relative py-14 md:py-20 bg-brand-bg"
        >
            <div className="mx-auto max-w-5xl px-6 md:px-10 lg:px-16">
                <div className="text-center mb-10">
                    <div className="flex items-center justify-center gap-3 mb-5">
                        <span className="h-px w-10 bg-brand-accent" />
                        <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-bold">
                            Find your match in 60 seconds
                        </span>
                        <span className="h-px w-10 bg-brand-accent" />
                    </div>
                    <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-primary leading-[1.05]">
                        Tell us a few details,
                        <br />
                        <em className="italic font-light text-brand-accent">
                            see your live rates.
                        </em>
                    </h2>
                    <p className="mt-5 text-lg text-brand-muted leading-relaxed max-w-2xl mx-auto">
                        A short, three-step form pulls real-time rates from our
                        whole panel of UK lenders. No credit check, no
                        obligation.
                    </p>
                </div>

                <div className="bg-brand-surface border border-brand-border rounded-[18px] shadow-[0_30px_60px_-25px_rgba(30,79,138,0.22)] p-6 md:p-10">
                    <RateDisclaimer className="mb-6" />
                    <div
                        ref={hostRef}
                        data-testid="mortgage-cta-form-host"
                        className="min-h-[320px]"
                    />
                </div>
            </div>
        </section>
    );
}
